import React, { useState } from 'react';
import { EnvelopeIcon, PhoneIcon, MapPinIcon, XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';

const Navbar = ({ logo }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navLinks = [
    { label: 'About Us',  id: 'about'    },
    { label: 'Services',  id: 'services' },
    { label: 'Projects',  id: 'projects' },
    { label: 'Our Team',  id: 'team'     },
  ];

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">

          {/* Logo and Company Name */}
          <a href="/" className="flex items-center space-x-3 group">
            <img src={logo} alt="MEK Logo" className="h-12 w-auto" />
            <div className="hidden lg:block">
              <span className="block text-xl font-bold text-gray-800 leading-none group-hover:text-green-700 transition">MEK</span>
              <span className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Development Consultants</span>
            </div>
          </a>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex space-x-8 text-sm font-medium">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => handleNavClick(e, link.id)}
                className="text-gray-600 hover:text-green-700 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right side: Contact CTA + Hamburger */}
          <div className="flex items-center gap-3">
            <button
              onClick={openModal}
              className="hidden sm:flex bg-green-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-green-800 transition-all shadow-md hover:shadow-lg items-center gap-2"
            >
              <EnvelopeIcon className="w-4 h-4" />
              Contact Us
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:text-green-700 hover:bg-green-50 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen
                ? <XMarkIcon className="w-6 h-6" />
                : <Bars3Icon className="w-6 h-6" />
              }
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-3 border-t border-gray-100 pt-4 pb-2 px-2 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => handleNavClick(e, link.id)}
                className="text-gray-700 hover:text-green-700 hover:bg-green-50 px-4 py-3 rounded-lg text-sm font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}

            <button
              onClick={() => { setIsMobileMenuOpen(false); openModal(); }}
              className="mt-2 flex items-center justify-center gap-2 bg-green-700 text-white px-5 py-3 rounded-lg text-sm font-semibold hover:bg-green-800 transition-all"
            >
              <EnvelopeIcon className="w-4 h-4" />
              Contact Us
            </button>
          </div>
        )}
      </nav>

      {/* Contact Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={closeModal}
          ></div>

          {/* Modal Card */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Green Header Bar */}
            <div className="bg-green-700 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <EnvelopeIcon className="w-5 h-5" />
                  Contact Us
                </h3>
                <button
                  onClick={closeModal}
                  className="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-green-100 text-sm mt-1">Get in touch with our team</p>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="space-y-5">

                {/* Office */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-green-100 rounded-xl flex items-center justify-center text-green-700">
                    <MapPinIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Lilongwe Office</p>
                    <p className="text-gray-600 text-sm mt-0.5">P.O. Box 143, Lilongwe, Malawi</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-green-100 rounded-xl flex items-center justify-center text-green-700">
                    <PhoneIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-1.5">Phone</p>
                    <a href="tel:+265995028207" className="block text-gray-600 text-sm hover:text-green-700 transition-colors py-0.5">
                      (+265) 995 028 207
                    </a>
                    <a href="tel:+265886300739" className="block text-gray-600 text-sm hover:text-green-700 transition-colors py-0.5">
                      (+265) 886 300 739
                    </a>
                    <a href="tel:+265995707813" className="block text-gray-600 text-sm hover:text-green-700 transition-colors py-0.5">
                      (+265) 995 707 813
                    </a>
                  </div>
                </div>

                {/* Email — two addresses */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-green-100 rounded-xl flex items-center justify-center text-green-700">
                    <EnvelopeIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm uppercase tracking-wide mb-1.5">Email</p>
                    <a
                      href="mailto:kmikwamba@gmail.com"
                      className="block text-green-700 hover:text-green-800 text-sm font-medium transition-colors py-0.5"
                    >
                      kmikwamba@gmail.com
                    </a>
                    <a
                      href="mailto:info@mekconsultantsmw.com"
                      className="block text-green-700 hover:text-green-800 text-sm font-medium transition-colors py-0.5"
                    >
                      info@mekconsultantsmw.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="my-6 border-t border-gray-100"></div>

              {/* Office Hours */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-700 text-sm font-semibold flex items-center gap-2">
                  <span>🕒</span> Office Hours
                </p>
                <p className="text-gray-500 text-xs mt-1">Monday - Friday: 8:00 AM - 5:00 PM</p>
                <button
                  onClick={() => window.location.href = 'mailto:info@mekconsultantsmw.com'}
                  className="mt-3 w-full bg-green-600 text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;